import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api, authStorage, setUnauthorizedHandler } from '../api';

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(authStorage.getToken());
  const [user, setUser] = useState(authStorage.getUser());
  const [bootstrapping, setBootstrapping] = useState(Boolean(authStorage.getToken()));

  const clearSession = () => {
    authStorage.clearSession();
    setToken(null);
    setUser(null);
  };

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.replace('/admin/login?expired=1');
      }
    });

    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const bootstrapSession = async () => {
      const savedToken = authStorage.getToken();
      if (!savedToken) {
        if (isMounted) setBootstrapping(false);
        return;
      }

      try {
        const response = await api.me();
        if (!isMounted) return;
        authStorage.setUser(response.data);
        setUser(response.data);
        setToken(savedToken);
      } catch {
        if (!isMounted) return;
        clearSession();
      } finally {
        if (isMounted) setBootstrapping(false);
      }
    };

    bootstrapSession();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = async (email, password) => {
    const response = await api.login(email, password);
    const data = response?.data;
    authStorage.setToken(data.token);
    authStorage.setUser(data.user);
    setToken(data.token);
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      // no-op
    }
    clearSession();
  };

  const refreshUser = async () => {
    const response = await api.me();
    authStorage.setUser(response.data);
    setUser(response.data);
    return response.data;
  };

  const value = useMemo(
    () => ({
      token,
      user,
      bootstrapping,
      isAuthenticated: Boolean(token),
      login,
      logout,
      refreshUser,
    }),
    [token, user, bootstrapping],
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
}
